<template>
  <form
    class="scanat-form"
    :class="{
      'scanat-form-horizontal': layout === 'horizontal',
      'scanat-form-vertical': layout === 'vertical'
    }"
    @submit.prevent="handleSubmit"
  >
    <slot></slot>
  </form>
</template>

<script setup>
import { ref, provide, reactive, onMounted, getCurrentInstance } from 'vue'

const {proxy: {
  Toast
}} = getCurrentInstance()

const props = defineProps({
  model: {
    type: Object,
    required: true
  },
  rules: {
    type: Object,
    default: () => ({})
  },
  layout: {
    type: String,
    default: 'vertical',
    validator: (value) => ['horizontal', 'vertical'].includes(value)
  },
  labelWidth: {
    type: String,
    default: undefined
  }
})

const emit = defineEmits(['submit', 'validate', 'failed'])

const formItems = reactive(new Map())
const errors = reactive({})

const registerFormItem = (name, item) => {
  formItems.set(name, item)
}

const unregisterFormItem = (name) => {
  formItems.delete(name)
}

const validateField = (name) => {
  const fieldRules = props.rules[name]
  if (!fieldRules) return true

  let valid = true
  errors[name] = ''

  for (const rule of fieldRules) {
    const value = props.model[name]
    
    if (rule.required && (value === undefined || value === null || value === '')) {
      errors[name] = rule.message
      if(rule.toast) {
        new Toast().toast('danger', rule.toast)
      }
      valid = false
      break
    }
    if (rule.validator && typeof rule.validator === 'function') {
      try {
        const result = rule.validator(value, props.model)
        if (result !== true) {
          errors[name] = typeof result === 'string' ? result : rule.message
          if(rule.toast) {
            new Toast().toast('danger', rule.toast)
          }
          valid = false
          break
        }
      } catch (error) {
        errors[name] = error.message || rule.message
        valid = false
        break
      }
    }
    
    if (rule.pattern && !rule.pattern.test(value)) {
      errors[name] = rule.message
      if(rule.toast) {
        new Toast().toast('danger', rule.toast)
      }
      valid = false
      break
    }
  }
  
  return valid
}

const validate = () => {
  let valid = true
  
  for (const name in props.rules) {
    const fieldValid = validateField(name)
    valid = valid && fieldValid
  }
  
  if (valid) {
    emit('validate', true)
  } else {
    emit('validate', false)
    emit('failed', errors)
  }
  
  return Promise.resolve(valid)
}

const resetValidation = () => {
  for (const name in errors) {
    errors[name] = ''
  }
}

const handleSubmit = () => {
  const valid = validate()
  
  if (valid) {
    emit('submit', props.model)
  }
}

defineExpose({
  validate,
  validateField,
  resetValidation
})

provide('formContext', {
  model: props.model,
  errors,
  rules: props.rules,
  layout: props.layout,
  labelWidth: props.labelWidth,
  validateField,
  registerFormItem,
  unregisterFormItem
})

provide('isFormItemNested', false)
</script>

<style scoped>
.scanat-form {
  width: 100%;
}

.scanat-form-horizontal {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.scanat-form-vertical {
  display: flex;
  flex-direction: column;
}
</style> 